/**
 * legacy/legacyBridge.ts — WorkRoamer OS v2 Migration: legacy engine bridge.
 *
 * MIGRATION SPRINT CONTEXT
 * The legacy mission stack (execution, sessions, trust, planner, memory,
 * persona, payments, sources, knowledge store, OCC config) has NO functional
 * v2 counterpart — the v2 engines cover travel intelligence (location,
 * fusion, predictions, community), not opportunity scoring. Rewriting the
 * mission pipeline is out of scope for a migration sprint ("do NOT add new
 * features / modules"). The honest v2 path is therefore:
 *
 *   1. Register every legacy engine with Roamer Core™ as an EngineAdapter
 *      (id suffixed `-legacy`) so there is ONE engine registry, ONE health
 *      monitor, and the Developer Center™ observes the whole runtime.
 *   2. Forward the legacy window CustomEvents onto the CoreEventBus so there
 *      is ONE event bus. Legacy emitters keep dispatching their original
 *      events (backward compatibility — nothing breaks); the bridge mirrors
 *      them as core events for v2 consumers.
 *   3. Components migrate consumption to the core surface over time; when a
 *      legacy engine loses its last direct importer it is retired.
 *
 * RULES
 * - The bridge only OBSERVES legacy engines. It never mutates their state,
 *   never re-implements their logic, and never intercepts their events.
 * - Health probes are REAL cheap reads (same policy as bootstrap.ts).
 *   Pure stateless function modules get no healthCheck — the registry
 *   treats them as always-on, which is genuinely true.
 * - Idempotent: safe to call more than once (guarded latches).
 */

import type { RoamerCore } from '../RoamerCore';
import type { EngineAdapter } from '../registry/EngineRegistry';

// Legacy engines (observed only — no mutation from the bridge)
import { loadExecutionState } from '@/services/MissionExecutionEngine';
import { getAllSessions } from '@/services/MissionSessionService';
import { loadMemory } from '@/services/OperatorMemoryService';
import { loadPersona } from '@/services/OperatorPersonaService';
import { getAllSources } from '@/services/MissionSourceRegistry';
import { getAllKnowledgeItems } from '@/services/KnowledgeStore';
import { loadOperatorConfig } from '@/lib/operatorConfig';

function probe(fn: () => unknown): boolean {
  try { fn(); return true; } catch { return false; }
}

async function probeAsync(fn: () => Promise<unknown>): Promise<boolean> {
  try { await fn(); return true; } catch { return false; }
}

/**
 * Legacy mission-stack engines registered under Roamer Core™ supervision.
 * `-legacy` id suffix is the retirement marker: when one of these loses its
 * last direct importer, delete the file AND its adapter here.
 */
export function buildLegacyEngineAdapters(): EngineAdapter[] {
  return [
    {
      id: 'mission-execution-legacy',
      name: 'Mission Execution Engine (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['execution-records', 'status-lifecycle', 'active-mission-counts'],
      healthCheck: () => probe(() => loadExecutionState()),
    },
    {
      id: 'mission-session-legacy',
      name: 'Mission Session Service (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['session-capture', 'session-lifecycle', 'learning-bridge'],
      healthCheck: () => probe(() => getAllSessions()),
    },
    {
      id: 'trust-legacy',
      name: 'Trust Engine (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['confidence-scoring', 'plain-english-reasons'],
      // Pure stateless function module (computeTrustReport) — always-on.
    },
    {
      id: 'mission-planner-legacy',
      name: 'Mission Planner Engine (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['mission-plans', 'step-generation'],
      // Pure stateless function module (computeMissionPlans) — always-on.
    },
    {
      id: 'operator-memory-legacy',
      name: 'Operator Memory Service (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['company-memory', 'experience-summary', 'additive-only'],
      healthCheck: () => probe(() => loadMemory()),
    },
    {
      id: 'operator-persona-legacy',
      name: 'Operator Persona Service (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['persona-profile'],
      healthCheck: () => probe(() => loadPersona()),
    },
    {
      id: 'occ-config-legacy',
      name: 'Operator Control Center Config (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['runtime-config', 'preference-weights'],
      healthCheck: () => probe(() => loadOperatorConfig()),
    },
    {
      id: 'mission-source-registry-legacy',
      name: 'Mission Source Registry (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['source-catalog', 'harvest-status', 'source-visits'],
      healthCheck: () => probe(() => getAllSources()),
    },
    {
      id: 'knowledge-store-legacy',
      name: 'Knowledge Store (Legacy)',
      version: '1.0.0-legacy',
      capabilities: ['knowledge-items', 'collections', 'blob-storage'],
      healthCheck: () => probeAsync(() => getAllKnowledgeItems()),
    },
  ];
}

// ─── Legacy CustomEvent → CoreEventBus forwarding ──────────────────────────────

/** Legacy window events mirrored onto the core bus (name → core event). */
const LEGACY_EVENT_FORWARDS: Array<{
  windowEvent: string;
  coreEvent: 'SystemStateChanged';
  reason: string;
}> = [
  // OCC save: legacy emitters keep dispatching; Home.tsx keeps its listener
  // (backward compatibility). v2 consumers subscribe to SystemStateChanged.
  { windowEvent: 'nomad:occ-saved', coreEvent: 'SystemStateChanged', reason: 'occ-saved' },
  // Legacy data mutations (execution/sessions/persona/memory saves) — the
  // save paths dispatch nomad:data-changed with {domain}; mirrored so live
  // screens (Sidebar, Home cards) update without polling.
  { windowEvent: 'nomad:data-changed', coreEvent: 'SystemStateChanged', reason: 'data-changed' },
  // Location changes are ALREADY on the bus via the builtin location adapter
  // (LocationChanged) — deliberately not duplicated here.
];

let eventsBridged = false;

/**
 * Mirror legacy window CustomEvents onto the CoreEventBus. Observe-only:
 * the original events keep firing and their listeners keep working.
 */
export function bridgeLegacyEvents(core: RoamerCore, nowFn: () => number = Date.now): void {
  if (eventsBridged) return;
  if (typeof window === 'undefined') return; // SSR / test safety
  for (const fwd of LEGACY_EVENT_FORWARDS) {
    window.addEventListener(fwd.windowEvent, (raw) => {
      const detail = (raw as CustomEvent).detail as Record<string, unknown> | undefined;
      core.publish(
        fwd.coreEvent,
        'legacy-bridge',
        { reason: fwd.reason, legacyEvent: fwd.windowEvent, ...(detail ?? {}) },
        nowFn(),
      );
    });
  }
  eventsBridged = true;
}
